import React, { useState, useEffect, useContext, useMemo } from "react";

import { UserContext } from "../../UserContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "../../store/groupsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { getManagerGroups } from "../../api/groupsService";
import { openPopup } from "../Common/popupUIStore";
import {
   readGroupManagerId,
   readGroupManagerName,
   readGroupProfessorId,
   readGroupProfessorName,
   readGroupSector,
   readGroupToken,
   readStudentGroupId,
} from "../../utils/groupFields";
import { ROLES, normalizeRole } from "../../auth/access";

import StudentItem from "../Common/StudentItem";
import IconButton from "../Common/IconButton";
import UIIcon from "../Common/UIIcon";
import SearchToggle from "../Common/SearchToggle";
import BookingStatusFilter from "../Common/BookingStatusFilter";
import {
   filterStudentsByBookingStatus,
   useBookingStatusMonitor,
} from "../../hooks/useBookingStatusMonitor";
import {
   bookingStatusLabel,
   bookingStatusShortLabel,
   bookingStatusTitle,
   bookingStatusTone,
} from "../../utils/bookingStatus";

/* ===================== Utils ===================== */
const GROUPS_PAGE_SIZE = 10;

const escapeRegExp = (s = "") =>
   String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightText = (text, query, highlightClassName) => {
   const t = String(text ?? "");
   const q = String(query ?? "");
   if (!q) return t;

   const safe = escapeRegExp(q);
   const parts = t.split(new RegExp(`(${safe})`, "gi"));

   return parts.map((part, index) =>
      part.toLowerCase() === q.toLowerCase() ? (
         <i key={index} className={highlightClassName}>
            {part}
         </i>
      ) : (
         part
      ),
   );
};

/* 🔒 token protejat = ABCD1234 */
const isProtectedToken = (token) =>
   String(token ?? "")
      .trim()
      .toUpperCase() === "ABCD1234";

/* ===== Student avatar helpers ===== */
const firstLetter = (v) =>
   String(v || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();

   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();

   const two = fn.slice(0, 2);
   if (two) return two.toUpperCase();

   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
   return h >>> 0;
}

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];

const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];

const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function getRandomAvatarColor() {
   return AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
}

function getAvatarColorFromName(student) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
   const hasLetter = /\p{L}/u.test(fullName);
   if (!hasLetter) return null;

   const normalized = fullName.normalize("NFKD");
   const idx = hashStringToUInt(normalized) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

/* Culoare generată dintr-un text oarecare (ex: numele grupei). */
function getColorFromString(str) {
   const s = String(str || "").trim();
   if (!s) return getRandomAvatarColor();
   const idx = hashStringToUInt(s.normalize("NFKD")) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

/* Inițialele grupei din numele ei. */
function getGroupInitials(name) {
   const words = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
   if (!words.length) return "–";
   if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
   return `${firstLetter(words[0])}${firstLetter(words[1])}`.toUpperCase();
}

const personName = (p) =>
   `${p?.firstName || ""} ${p?.lastName || ""}`.trim();

function GroupManager() {
   const { user } = useContext(UserContext);
   const dispatch = useDispatch();

   const groupsState = useSelector((state) => state.groups || {});
   const usersState = useSelector((state) => state.users || {});
   const groups = Array.isArray(groupsState.list) ? groupsState.list : [];
   const users = useMemo(() => {
      const merged = new Map();
      const fromGroups = Array.isArray(groupsState.users)
         ? groupsState.users
         : [];
      const fromUsers = Array.isArray(usersState.list) ? usersState.list : [];
      for (const u of [...fromGroups, ...fromUsers]) {
         const id = u?.id ?? u?.userId ?? u?.user?.id;
         if (id == null) continue;
         merged.set(String(id), u?.user && u.user.id != null ? u.user : u);
      }
      return Array.from(merged.values());
   }, [groupsState.users, usersState.list]);

   const isAdmin = normalizeRole(user?.role) === ROLES.ADMIN;

   // Aceeași stare a programării ca în lista de studenți, pentru membrii grupei.
   const canSeeBookingStatus =
      normalizeRole(user?.role) === ROLES.ADMIN ||
      normalizeRole(user?.role) === ROLES.MANAGER;
   const bookingMonitor = useBookingStatusMonitor(canSeeBookingStatus);
   const [statusFilter, setStatusFilter] = useState(null);

   const visibleGroups = useMemo(
      () => groups.filter((g) => !isProtectedToken(readGroupToken(g))),
      [groups],
   );

   const managers = useMemo(
      () => users.filter((u) => normalizeRole(u?.role) === "MANAGER"),
      [users],
   );

   const usersById = useMemo(() => {
      const map = new Map();
      for (const u of users) {
         const person = u?.user && (u.user.id != null || u.user.userId != null)
            ? u.user
            : u;
         for (const key of [person?.id, person?.userId, u?.id, u?.userId]) {
            if (key != null && key !== "") map.set(String(key), person);
         }
      }
      return map;
   }, [users]);

   // Fallback când GET /groups nu include managerId / professorId.
   const [managerByGroupId, setManagerByGroupId] = useState({});
   const [assignmentByGroupId, setAssignmentByGroupId] = useState({});

   useEffect(() => {
      if (!managers.length) {
         setManagerByGroupId({});
         setAssignmentByGroupId({});
         return undefined;
      }

      let cancelled = false;
      (async () => {
         const entries = await Promise.all(
            managers.map(async (m) => {
               try {
                  const arr = await getManagerGroups(m.id);
                  return { manager: m, groups: arr };
               } catch {
                  return { manager: m, groups: [] };
               }
            }),
         );
         if (cancelled) return;
         const map = {};
         const extras = {};
         for (const { manager, groups: assigned } of entries) {
            for (const g of assigned) {
               const gid = String(g?.id ?? g?.groupId ?? "");
               if (!gid) continue;
               map[gid] = manager;
               extras[gid] = g;
            }
         }
         setManagerByGroupId(map);
         setAssignmentByGroupId(extras);
      })();

      return () => {
         cancelled = true;
      };
   }, [managers]);

   const [search, setSearch] = useState(() => ({
      open:
         typeof window !== "undefined" &&
         window.matchMedia("(min-width: 769px)").matches,
      query: "",
   }));
   const [viewMode, setViewMode] = useState({ mode: "list", group: null });

   useEffect(() => {
      dispatch(fetchGroups());
      dispatch(fetchUsers());
   }, [dispatch, user?.id]);

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia("(min-width: 769px)");
      const syncDesktopSearch = (event) => {
         if (!event.matches) return;
         setSearch((prev) => (prev.open ? prev : { ...prev, open: true }));
      };

      syncDesktopSearch(mq);

      if (mq.addEventListener) mq.addEventListener("change", syncDesktopSearch);
      else mq.addListener(syncDesktopSearch);

      return () => {
         if (mq.removeEventListener) {
            mq.removeEventListener("change", syncDesktopSearch);
         } else {
            mq.removeListener(syncDesktopSearch);
         }
      };
   }, []);

   const groupedUsersByGroup = useMemo(() => {
      return visibleGroups.map((group) => {
         const extra = assignmentByGroupId[String(group.id)] || {};
         return {
            ...extra,
            ...group,
            professorId:
               group.professorId ?? extra.professorId ?? extra.professor?.id,
            managerId: group.managerId ?? extra.managerId,
            professor: group.professor ?? extra.professor,
            members: users.filter((u) => {
               const role = normalizeRole(u?.role);
               if (role && role !== "USER") return false;
               return String(readStudentGroupId(u) || "") === String(group.id);
            }),
         };
      });
   }, [visibleGroups, users, assignmentByGroupId]);

   // Căutarea din listă e după STUDENT: păstrăm doar grupele care conțin cel
   // puțin un student ce se potrivește (nume, telefon, email, IDNP). Grupa
   // ABCD1234 e deja exclusă din groupedUsersByGroup (token protejat).
   // Filtrul după stare se pune o singură dată, din lista de grupe: lista
   // arată doar grupele cu elevi în acea stare, iar la deschiderea unei grupe
   // membrii sunt deja filtrați.
   const groupsByStatus = useMemo(() => {
      if (!statusFilter) return groupedUsersByGroup;
      return groupedUsersByGroup
         .map((group) => {
            const matching = filterStudentsByBookingStatus(
               group.members || [],
               bookingMonitor.byUser,
               statusFilter,
            );
            return { ...group, matchingMembersCount: matching.length };
         })
         .filter((group) => group.matchingMembersCount > 0);
   }, [bookingMonitor, groupedUsersByGroup, statusFilter]);

   const filteredGroups = useMemo(() => {
      const q = String(search.query || "")
         .trim()
         .toLowerCase();
      if (!q) return groupsByStatus;

      return groupsByStatus.filter((group) =>
         (group.members || []).some((student) =>
            [
               student?.firstName,
               student?.lastName,
               student?.email,
               student?.phone,
               student?.idnp,
            ]
               .filter(Boolean)
               .join(" ")
               .toLowerCase()
               .includes(q),
         ),
      );
   }, [groupsByStatus, search.query]);

   const [visibleGroupCount, setVisibleGroupCount] = useState(GROUPS_PAGE_SIZE);

   useEffect(() => {
      setVisibleGroupCount(GROUPS_PAGE_SIZE);
   }, [search.query, statusFilter, viewMode.mode]);

   const pagedGroups = useMemo(
      () => filteredGroups.slice(0, visibleGroupCount),
      [filteredGroups, visibleGroupCount],
   );

   const totalGroupResults = filteredGroups.length;
   const canLoadMoreGroups = visibleGroupCount < totalGroupResults;
   const nextGroupBatch = Math.min(
      GROUPS_PAGE_SIZE,
      Math.max(0, totalGroupResults - visibleGroupCount),
   );

   const handleLoadMoreGroups = () => {
      setVisibleGroupCount((count) =>
         Math.min(count + GROUPS_PAGE_SIZE, totalGroupResults),
      );
   };

   const detailGroup =
      viewMode.mode === "details" && viewMode.group
         ? groupedUsersByGroup.find(
              (group) => String(group.id) === String(viewMode.group.id),
           ) || viewMode.group
         : null;

   const detailMembers = useMemo(
      () => detailGroup?.members ?? [],
      [detailGroup],
   );

   const filteredDetailMembers = useMemo(() => {
      const byStatus = filterStudentsByBookingStatus(
         detailMembers,
         bookingMonitor.byUser,
         statusFilter,
      );
      const q = String(search.query || "")
         .trim()
         .toLowerCase();

      if (!q) return byStatus;

      return byStatus.filter((student) =>
         [
            student?.firstName,
            student?.lastName,
            student?.email,
            student?.phone,
            student?.idnp,
         ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q),
      );
   }, [bookingMonitor, detailMembers, search.query, statusFilter]);

   // Dacă grupa curentă devine invizibilă (token protejat), revenim la listă.
   useEffect(() => {
      if (viewMode.mode !== "details" || !viewMode.group) return;
      const stillVisible = visibleGroups.some(
         (g) => String(g.id) === String(viewMode.group.id),
      );
      if (!stillVisible) setViewMode({ mode: "list", group: null });
   }, [viewMode, visibleGroups]);

   const openCreate = () => {
      openPopup("groupEdit", { mode: "create" });
   };

   const handleOpenStudentPopup = (student) => {
      openPopup("studentDetails", { student });
   };

   const openGroupDetailsFromSearch = (group) => {
      if (!group) return;
      // Păstrăm căutarea curentă ca în grupă să apară doar studenții găsiți.
      setViewMode({ mode: "details", group });
   };

   const openGroupEditFromSearch = (group) => {
      if (!group) return;
      setSearch((prev) => ({ ...prev, query: "" }));
      openPopup("groupEdit", { groupId: group.id, mode: "edit" });
   };

   const handleToggleSearch = () => {
      setSearch((prev) =>
         prev.open ? { open: false, query: "" } : { ...prev, open: true },
      );
   };

   const detailColorByKey = useMemo(() => {
      const m = new Map();
      detailMembers.forEach((s, idx) => {
         const key = String(s.id ?? s.phone ?? s.email ?? `__idx_${idx}`);
         const det = getAvatarColorFromName(s);
         m.set(key, det || getRandomAvatarColor());
      });
      return m;
   }, [detailMembers]);

   return (
      <div className="studentsGroupsUI">
         <div
            className={`studentsGroupsUI__header ${search.open ? "is-open" : ""}`}
         >
            <h2 className="studentsGroupsUI__title">Grupe</h2>

            <div className="studentsGroupsUI__right">
               <SearchToggle
                  open={search.open}
                  value={search.query}
                  onValueChange={(val) =>
                     setSearch((s) => ({ ...s, query: val }))
                  }
                  onToggle={handleToggleSearch}
                  placeholder={
                     viewMode.mode === "details"
                        ? "Caută student în grupa curentă..."
                        : "Caută student (nume, telefon, email)..."
                  }
                  wrapperClassName="studentsGroupsUI__search"
                  inputClassName="studentsGroupsUI__inputSearch"
                  buttonClassName="studentsGroupsUI__iconBtn"
                  iconClassName={`studentsGroupsUI__icon ${search.open ? "is-rotated" : ""}`}
                  titleOpen="Închide căutarea"
                  titleClosed="Caută"
               />

               {isAdmin ? (
                  <IconButton
                     className="studentsGroupsUI__iconBtn"
                     icon="add"
                     iconClassName="studentsGroupsUI__icon"
                     onClick={openCreate}
                     title="Adaugă grupă"
                  />
               ) : null}
               {bookingMonitor.enabled ? (
                  <BookingStatusFilter
                     value={statusFilter}
                     onChange={setStatusFilter}
                     className="studentsGroupsUI__statusFilter"
                  />
               ) : null}
            </div>
         </div>

         <div className="studentsGroupsUI__gridWrap">
            <div className="studentsGroupsUI__grid">
               {/* ===== LISTĂ GRUPE ===== */}
               {viewMode.mode === "list" &&
                  pagedGroups.map((group) => {
                     const pLabel =
                        readGroupProfessorName(group, usersById) || "—";

                     const mappedManager =
                        managerByGroupId[String(group.id)] || null;
                     const mLabel =
                        readGroupManagerName(group, usersById) ||
                        (mappedManager ? personName(mappedManager) : "") ||
                        "—";

                     const sectorLabel = readGroupSector(group);
                     const groupToken = readGroupToken(group);
                     const avatarColor = getColorFromString(group.name);
                     const avatarInitials = getGroupInitials(group.name);

                     return (
                        <div key={group.id} className="studentsGroupsUI__item">
                           <div className="studentsGroupsUI__itemLeft">
                              <div
                                 className="studentsGroupsUI__avatar"
                                 style={{ background: avatarColor }}
                                 aria-hidden="true"
                              >
                                 {avatarInitials}
                              </div>

                              <div className="studentsGroupsUI__itemInfo">
                                 <div className="studentsGroupsUI__itemTop">
                                    <h3>
                                       {highlightText(
                                          group.name,
                                          search.query,
                                          "studentsGroupsUI__highlight",
                                       )}
                                    </h3>
                                    <p className="studentsGroupsUI__itemKey">
                                       <UIIcon
                                          name="key"
                                          className="studentsGroupsUI__keyIcon"
                                       />
                                       {highlightText(
                                          groupToken || "—",
                                          search.query,
                                          "studentsGroupsUI__highlight",
                                       )}
                                    </p>
                                 </div>
                              </div>
                           </div>

                           <div className="studentsGroupsUI__pills">
                              <div className="studentsGroupsUI__pillsRow">
                                 <span
                                    className="studentsGroupsUI__pill studentsGroupsUI__pill--professor"
                                    title="Profesor"
                                 >
                                    <UIIcon
                                       name="graduate"
                                       className="studentsGroupsUI__pillIcon"
                                    />
                                    <span className="studentsGroupsUI__pillValue">
                                       {highlightText(
                                          pLabel,
                                          search.query,
                                          "studentsGroupsUI__highlight",
                                       )}
                                    </span>
                                 </span>
                                 <span
                                    className="studentsGroupsUI__pill studentsGroupsUI__pill--sector"
                                    title="Sector"
                                 >
                                    <UIIcon
                                       name="map"
                                       className="studentsGroupsUI__pillIcon"
                                    />
                                    <span className="studentsGroupsUI__pillValue">
                                       {sectorLabel || "—"}
                                    </span>
                                 </span>
                              </div>
                              <div className="studentsGroupsUI__pillsRow">
                                 <span
                                    className="studentsGroupsUI__pill studentsGroupsUI__pill--manager"
                                    title="Manager"
                                 >
                                    <UIIcon
                                       name="manager"
                                       className="studentsGroupsUI__pillIcon"
                                    />
                                    <span className="studentsGroupsUI__pillValue">
                                       {highlightText(
                                          mLabel,
                                          search.query,
                                          "studentsGroupsUI__highlight",
                                       )}
                                    </span>
                                 </span>
                                 <span
                                    className="studentsGroupsUI__pill studentsGroupsUI__pill--members"
                                    title={
                                       statusFilter
                                          ? `${group.matchingMembersCount} din ${group.members.length} membri în starea aleasă`
                                          : "Membri"
                                    }
                                 >
                                    <UIIcon
                                       name="group"
                                       className="studentsGroupsUI__pillIcon"
                                    />
                                    <span className="studentsGroupsUI__pillValue">
                                       {statusFilter
                                          ? `${group.matchingMembersCount}/${group.members.length}`
                                          : group.members.length}
                                    </span>
                                 </span>
                              </div>
                           </div>

                           <div className="studentsGroupsUI__itemActions">
                              <button
                                 type="button"
                                 className="studentsGroupsUI__actionBtn studentsGroupsUI__actionBtn--edit"
                                 onClick={() => openGroupEditFromSearch(group)}
                              >
                                 Editează
                              </button>
                              <button
                                 type="button"
                                 className="studentsGroupsUI__actionBtn studentsGroupsUI__actionBtn--view"
                                 onClick={() =>
                                    openGroupDetailsFromSearch(group)
                                 }
                              >
                                 Vezi
                              </button>
                           </div>
                        </div>
                     );
                  })}

               {/* ===== STARE GOALĂ (listă) ===== */}
               {viewMode.mode === "list" && filteredGroups.length === 0 && (
                  <p className="studentsGroupsUI__empty">
                     {String(search.query || "").trim()
                        ? "Nicio grupă cu acest student."
                        : statusFilter
                          ? "Nicio grupă cu elevi în această stare."
                          : "Nu există grupe."}
                  </p>
               )}

               {/* ===== PAGINARE GRUPE ===== */}
               {viewMode.mode === "list" && canLoadMoreGroups && (
                  <div className="studentsGroupsUI__footer">
                     <span className="studentsGroupsUI__counter">
                        Afișate{" "}
                        {Math.min(visibleGroupCount, totalGroupResults)} din{" "}
                        {totalGroupResults}
                     </span>
                     <button
                        type="button"
                        className="studentsGroupsUI__loadMore"
                        onClick={handleLoadMoreGroups}
                     >
                        Afișează încă {nextGroupBatch}
                     </button>
                  </div>
               )}

               {/* ===== DETALII GRUP ===== */}
               {viewMode.mode === "details" && viewMode.group && (
                  <>
                     <button
                        type="button"
                        className="studentsGroupsUI__backBtn"
                        onClick={() => {
                           setSearch((prev) => ({ ...prev, query: "" }));
                           setViewMode({ mode: "list", group: null });
                        }}
                     >
                        Înapoi la grupe
                     </button>

                     {filteredDetailMembers.length > 0 ? (
                        filteredDetailMembers.map((student, idx) => {
                           const key = String(
                              student.id ??
                                 student.phone ??
                                 student.email ??
                                 `__idx_${idx}`,
                           );
                           const color =
                              detailColorByKey.get(key) ||
                              getRandomAvatarColor();
                           const bookingState = bookingMonitor.stateOf(student);

                           return (
                              <StudentItem
                                 key={student.id ?? key}
                                 student={student}
                                 color={color}
                                 initials={getInitials(student)}
                                 onOpen={handleOpenStudentPopup}
                                 highlightQuery={search.query}
                                 secondaryText={student.phone || "–"}
                                 phoneCopy
                                 statusText={bookingStatusShortLabel(bookingState)}
                                 statusTone={bookingStatusTone(bookingState)}
                                 statusTitle={
                                    bookingState
                                       ? `${bookingStatusLabel(bookingState)} — ${bookingStatusTitle(bookingState)}`
                                       : ""
                                 }
                              />
                           );
                        })
                     ) : (
                        <p className="studentsGroupsUI__empty">
                           {statusFilter
                              ? "Niciun student cu această stare în grupă."
                              : search.query.trim()
                                ? "Nu există studenți care să se potrivească."
                                : "Nu sunt studenți în această grupă."}
                        </p>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   );
}

export default GroupManager;
